// src/components/LoginPage.jsx
//
// Standalone branded login page shown when the user's session is absent
// or expired (App.jsx catches the 401 from /api/me and redirects here).
// Clicking "Sign in with Okta" forwards to /api/login, which starts the
// Okta OIDC flow. The returnTo param is preserved end-to-end so the user
// lands back where they started after a successful login.
//
// Design: matches AccessDeniedPage — same header gradient, same centred card.

import React from "react";
import { T } from "../app/tokens.js";
import { APP_NAME } from "../constants/branding.js";

const TRISKELION_PATH =
  "M88.6772 66.251C84.8472 64.031 80.5272 63.391 76.2472 63.251C72.6572 63.131 " +
  "67.3072 60.831 67.3072 54.251C67.3278 51.8808 68.2744 49.6126 69.9448 47.9309C" +
  "71.6153 46.2493 73.8771 45.2875 76.2472 45.251C80.5272 45.101 84.8472 44.461 " +
  "88.6772 42.251C92.1161 40.2597 94.9706 37.3986 96.9541 33.9552C98.9375 30.5117 " +
  "99.9801 26.607 99.9771 22.6331C99.9742 18.6592 98.9258 14.756 96.9372 11.3155C" +
  "94.9486 7.87501 92.0898 5.01823 88.6479 3.03205C85.2059 1.04586 81.3021 " +
  "0.000181153 77.3282 2.3535e-08C73.3543 -0.000181106 69.4503 1.04514 66.0082 " +
  "3.03102C62.5661 5.01689 59.7071 7.87341 57.7182 11.3137C55.7293 14.7541 " +
  "54.6805 18.6571 54.6772 22.631C54.6772 27.021 56.2372 31.061 58.2372 34.811C" +
  "59.9072 37.961 60.7572 43.811 55.0072 47.121C52.9289 48.2774 50.4803 48.5733 " +
  "48.1864 47.9452C45.8926 47.317 43.9363 45.8149 42.7372 43.761C40.4872 40.161 " +
  "37.7372 36.761 33.9672 34.591C30.5238 32.6048 26.6183 31.5598 22.6432 31.561C" +
  "18.6681 31.5622 14.7633 32.6095 11.3211 34.5978C7.87891 36.5861 5.02062 " +
  "39.4452 3.03339 42.888C1.04617 46.3307 0 50.2359 0 54.211C0 58.1862 1.04617 " +
  "62.0913 3.03339 65.534C5.02062 68.9768 7.87891 71.836 11.3211 73.8242C14.7633 " +
  "75.8125 18.6681 76.8598 22.6432 76.861C26.6183 76.8622 30.5238 75.8172 " +
  "33.9672 73.831C37.7672 71.631 40.4872 68.261 42.7372 64.661C44.5872 61.661 " +
  "49.1972 57.941 55.0072 61.301C57.0479 62.5235 58.5281 64.4972 59.1302 " +
  "66.7987C59.7322 69.1001 59.408 71.5459 58.2272 73.611C56.2272 77.361 54.6772 " +
  "81.401 54.6772 85.791C54.6774 89.7681 55.7245 93.6751 57.713 97.1194C59.7016 " +
  "100.564 62.5616 103.424 66.0057 105.413C69.4498 107.401 73.3568 108.449 " +
  "77.3339 108.449C81.311 108.45 85.2182 107.404 88.6629 105.416C92.1076 " +
  "103.428 94.9684 100.569 96.958 97.1251C98.9475 93.6814 99.9957 89.7747 " +
  "99.9972 85.7976C99.9986 81.8205 98.9533 77.9131 96.9662 74.4679C94.9792 " +
  "71.0228 92.1204 68.1613 88.6772 66.171V66.251Z";

export function LoginPage() {
  const params   = new URLSearchParams(window.location.search);
  const returnTo = params.get("returnTo") || "/";
  const loginHref = `/api/login?returnTo=${encodeURIComponent(returnTo)}`;

  return (
    <div
      data-testid="login-page"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: T.bg,
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {/* Brand header */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 28px",
          background: `linear-gradient(to right, ${T.hdrFrom} 0%, ${T.hdrMid} 55%, ${T.hdrTo} 100%)`,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          gap: 10,
          flexShrink: 0,
          color: T.white,
        }}
      >
        <svg
          width="18"
          height="20"
          viewBox="0 0 100 109"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ flexShrink: 0, display: "block" }}
        >
          <path d={TRISKELION_PATH} fill="white" />
        </svg>
        <span style={{ fontSize: T.fsMd, fontWeight: T.fwBold, lineHeight: "20px" }}>
          {APP_NAME}
        </span>
      </header>

      {/* Login card */}
      <main
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 16px",
        }}
      >
        <div
          style={{
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: 12,
            boxShadow: T.shadowCardMd,
            padding: "40px 48px",
            maxWidth: 420,
            width: "100%",
            textAlign: "center",
          }}
        >
          {/* Triskelion icon in a blue soft disc */}
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: T.blueSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <svg
              width="26"
              height="28"
              viewBox="0 0 100 109"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d={TRISKELION_PATH} fill={T.blue} />
            </svg>
          </div>

          <h1
            style={{
              fontSize: T.fsLgX,
              fontWeight: T.fwBold,
              color: T.dark,
              margin: "0 0 8px",
            }}
          >
            Sign in to {APP_NAME}
          </h1>

          <p
            style={{
              fontSize: T.fsMd,
              color: T.mid,
              lineHeight: 1.6,
              margin: "0 0 28px",
            }}
          >
            Use your Okta account to access the platform.
          </p>

          <a
            href={loginHref}
            data-testid="login-page-sign-in"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: T.blue,
              color: T.white,
              padding: "9px 24px",
              borderRadius: 6,
              fontSize: T.fsMd,
              fontWeight: T.fwSemibold,
              textDecoration: "none",
              lineHeight: "20px",
            }}
          >
            Sign in with Okta
          </a>
        </div>
      </main>
    </div>
  );
}
